import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styles from './ProducerProjectsPage.module.css';
import ProducerContentContainer from '../layout/ProducerContentContainer';
import ApplicantView from './ApplicantView';
import { getMyCastingList, changeCastingStatus, deleteCasting } from '../../../api/castingApi';
import { postingStatusKey, postingStatusLabel, isOpenStatus } from '../../../shared/castings/postingStatus';
import { getProducerDashboardSummary } from '../../../api/producerDashboardApi';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import { useAuth } from '../../../contexts/AuthContext';

/* ── 상수 ── */
/** 탭 이름 ↔ 공고 상태 키. 서버 철자가 아니라 키로 비교한다 */
const TAB_TO_STATUS = { '모집중': 'OPEN', '마감': 'CLOSED', '임시저장': 'DRAFT' };

/* ── 아이콘 ── */
function IcoCalendar() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="18" rx="2" stroke="#A2A2A2" strokeWidth="1.5"/>
      <path d="M16 2v4M8 2v4M3 10h18" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function IcoPeopleSm() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="9" cy="7" r="4" stroke="#A2A2A2" strokeWidth="1.5"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

const ICON_APPLICANT = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#FFA40D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="9" cy="7" r="4" stroke="#FFA40D" strokeWidth="1.8" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#FFA40D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ICON_VIEW = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <ellipse cx="12" cy="12" rx="9" ry="6" stroke="#6366F1" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="2.5" stroke="#6366F1" strokeWidth="1.8" />
  </svg>
);

/* ── StatCard ── */
function StatCard({ label, value, icon }) {
  return (
    <div className={styles.statCard}>
      <span className={styles.statCardValue}>{value.toLocaleString()}</span>
      <div className={styles.statCardRow}>
        {icon}
        <span className={styles.statCardLabel}>{label}</span>
      </div>
    </div>
  );
}

/* ── ProjectCard ── */
function ProjectCard({ project, onManage, menuOpen, setMenuOpen, onClose, onDelete, onEdit }) {
  const isDraft = project.statusKey === 'DRAFT';
  const menuItems = isDraft
    ? [{ label: '삭제', red: true, action: () => { onDelete(project.id); setMenuOpen(null); } }]
    : [
        { label: '수정', red: false, action: () => { setMenuOpen(null); onEdit(project.id); } },
        { label: '마감', red: false, action: () => { onClose(project.id); setMenuOpen(null); } },
        { label: '삭제', red: true,  action: () => { onDelete(project.id); setMenuOpen(null); } },
      ];

  return (
    <div className={styles.projectCard}>
      <div className={styles.projectCardTop}>
        <div className={styles.projectCardBadges}>
          <span className={styles.badgeGenre}>{project.genre}</span>
          <span className={styles.badgeStatus}>{project.status}</span>
        </div>
        <div className={styles.menuWrapper}>
          <button
            onClick={e => { e.stopPropagation(); setMenuOpen(menuOpen === project.id ? null : project.id); }}
            className={styles.menuTriggerBtn}
          >
            ···
          </button>
          {menuOpen === project.id && (
            <div onClick={e => e.stopPropagation()} className={styles.menuDropdown}>
              {menuItems.map(({ label, red, action }) => (
                <button key={label} onClick={action} className={red ? styles.menuDropdownItemRed : styles.menuDropdownItem}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.projectCardTitle}>{project.title}</div>

      <div className={styles.projectCardInfoRow}>
        <div className={styles.projectCardInfoItem}>
          <IcoCalendar />
          <span className={styles.projectCardInfoText}>마감 {project.deadline && project.deadline !== '-' ? project.deadline : '미정'}</span>
        </div>
        <div className={styles.projectCardInfoDivider} />
        <div className={styles.projectCardInfoItem}>
          <IcoPeopleSm />
          <span className={styles.projectCardInfoText}>{project.applied}명 지원</span>
        </div>
      </div>

      <div className={styles.projectCardRolesRow}>
        <span className={styles.projectCardRolesLabel}>모집 역할 :</span>
        {project.roles.map(r => (
          <span key={r} className={styles.roleChip}>{r}</span>
        ))}
      </div>

      <div className={styles.projectCardCTA}>
        {isDraft ? (
          <button onClick={() => onEdit(project.id)} className={styles.manageBtn}>
            수정하기
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        ) : (
          <button onClick={() => onManage(project)} className={styles.manageBtn}>
            지원자 관리
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function ProducerProjectsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('전체');
  const [managingProject, setManagingProject] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const { isManagedAccount } = useAuth();
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [actionError, setActionError] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLoadingProjects(true);
    getMyCastingList().then(data => {
      const list = data?.content ?? [];
      const mapped = list.map(c => ({
        id: c.id,
        genre: c.category ?? c.projectType ?? '—',
        // 라벨만 들고 있으면 서버 철자가 조금만 달라도 탭이 걸러내지 못한다
        statusKey: postingStatusKey(c.status),
        status: postingStatusLabel(c.status),
        title: c.title,
        deadline: c.applicationDeadline ?? '-',
        applied: c.applicantCount ?? 0,
        views: c.viewCount ?? 0,
        roles: c.roles?.map(r => r.roleName) ?? [],
      }));
      setProjects(mapped);
      const { managingProjectId, managingProjectTitle } = location.state || {};
      if (managingProjectId) {
        const found = mapped.find(p => p.id === managingProjectId);
        setManagingProject(found ?? { id: managingProjectId, title: managingProjectTitle || '' });
      }
    }).catch(() => {}).finally(() => setLoadingProjects(false));

    getProducerDashboardSummary().then(setSummary).catch(() => {});
  }, []);

  /**
   * 마감·삭제는 서버가 받아들였을 때만 화면에서 치운다.
   *
   * 전에는 요청 결과를 보지 않고 화면만 바꿨다. 실패해도 마감된 것처럼 보이다가
   * 새로고침하면 되살아나서, 왜 안 바뀌는지 알 수 없었다.
   */
  const handleClose = async (id) => {
    const before = projects;
    setActionError(null);
    setProjects(prev => prev.map(p => (
      p.id === id ? { ...p, statusKey: 'CLOSED', status: '마감' } : p
    )));
    try {
      await changeCastingStatus(id, 'CLOSED');
    } catch (err) {
      setProjects(before);
      setActionError(`공고를 마감하지 못했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    }
  };

  const handleDelete = async (id) => {
    const before = projects;
    setActionError(null);
    setProjects(prev => prev.filter(p => p.id !== id));
    try {
      await deleteCasting(id);
    } catch (err) {
      setProjects(before);
      setActionError(`공고를 삭제하지 못했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    }
  };

  const TABS = ['전체', '모집중', '마감', '임시저장'];
  const filtered = tab === '전체'
    ? projects
    : projects.filter(p => (
      // '모집중' 탭에는 기획·제작 중인 공고도 들어간다 — 모두 지원을 받는 상태다
      TAB_TO_STATUS[tab] === 'OPEN' ? isOpenStatus(p.statusKey) : p.statusKey === TAB_TO_STATUS[tab]
    ));

  const totalViews = projects.reduce((sum, p) => sum + (p.views || 0), 0);
  const totalApplied = projects.reduce((sum, p) => sum + (p.applied || 0), 0);

  /**
   * 대행 계정은 맡은 공고만 다루므로 '공고가 몇 개인지'는 의미가 없다.
   * 그 공고가 얼마나 보였고 몇 명이 지원했는지를 대신 보여준다.
   * (대시보드가 숨겨져 있어 이 화면이 유일한 현황판이다)
   */
  const managedStats = [
    { label: '총 조회수', value: totalViews, icon: ICON_VIEW },
    { label: '총 지원자 수', value: totalApplied, icon: ICON_APPLICANT },
    { label: '오늘 지원자 수', value: summary?.todayApplicants ?? 0, icon: ICON_APPLICANT },
  ];

  const stats = [
    {
      label: '총 공고 수',
      value: summary?.totalCastings ?? projects.length,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
    {
      label: '진행 중인 공고 수',
      value: summary?.activeCastings ?? projects.filter(p => p.status === '모집중').length,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><circle cx="19" cy="19" r="4" fill="#10B981"/><path d="M19 17v2l1 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/></svg>,
    },
    {
      label: '오늘 지원자 수',
      value: summary?.todayApplicants ?? 0,
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#FFA40D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="#FFA40D" strokeWidth="1.8"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#FFA40D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
    {
      label: '총 지원자 수',
      value: projects.reduce((sum, p) => sum + (p.applied || 0), 0),
      icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#FFA40D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><circle cx="9" cy="7" r="4" stroke="#FFA40D" strokeWidth="1.8"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#FFA40D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    },
  ];

  return (
    <div onClick={() => setMenuOpen(null)}>
      {managingProject ? (
        <ApplicantView project={managingProject} onBack={() => setManagingProject(null)} />
      ) : (
        <ProducerContentContainer>
          <div style={{ padding: '50px 40px 40px' }}>
            <div className={styles.pageHeader}>
              <div className={styles.pageHeaderLeft}>
                <h1 className={styles.pageTitle}>공고·지원자 관리</h1>
                <div className={styles.pageSubtitle}>
                  {isManagedAccount ? '맡고 계신 공고의 현황과 지원자를 확인하세요' : '진행 중인 캐스팅 공고를 관리하세요'}
                </div>
              </div>
              {/* 대행 계정은 공고를 직접 만들지 않는다 (서버도 생성을 막는다) */}
              {!isManagedAccount && (
                <button className={styles.newProjectBtn} onClick={() => navigate('/producer/castings/new')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="#090909" strokeWidth="2.5" strokeLinecap="round"/></svg>
                  새 공고 만들기
                </button>
              )}
            </div>
          </div>

          <div style={{ padding: '0 40px 60px' }}>
            <div className={styles.statsCard}>
              {(isManagedAccount ? managedStats : stats).map(({ label, value, icon }) => (
                <StatCard key={label} label={label} value={value} icon={icon} />
              ))}
            </div>

            <div className={styles.tabsRow}>
              {TABS.map(tb => (
                <button key={tb} onClick={() => setTab(tb)} className={`${styles.tabBtn} ${tab === tb ? styles.tabBtnActive : ''}`}>
                  {tb}
                </button>
              ))}
            </div>

            {actionError && (
              <div role="alert" style={{
                margin: '0 0 12px', padding: '10px 14px', borderRadius: 8,
                border: '1px solid #FECACA', background: '#FEF2F2',
                color: '#B91C1C', fontSize: 13,
              }}>
                {actionError}
              </div>
            )}

            {loadingProjects ? (
              <div className={styles.projectsEmpty} style={{ display: 'flex', justifyContent: 'center' }}>
                <LoadingSpinner size="md" />
              </div>
            ) : filtered.length === 0 ? (
              <div className={styles.projectsEmpty}>
                <span className={styles.projectsEmptyText}>등록된 공고가 없습니다.</span>
              </div>
            ) : (
              <div className={styles.projectsGrid}>
                {filtered.map(project => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onManage={setManagingProject}
                    menuOpen={menuOpen}
                    setMenuOpen={setMenuOpen}
                    onEdit={(id) => navigate(`/producer/castings/edit/${id}`)}
                    onClose={handleClose}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </ProducerContentContainer>
      )}
    </div>
  );
}
